import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import { toMillis } from '@/lib/toMillis';
import type { ReviewAnswer, ReviewCheckDef } from '@/components/ReviewQueue/reviewChecks';

/**
 * The comprehension-check feed for the Review Queue: authored questions
 * (`visible_orientation_checks`), answer keys
 * (`visible_orientation_check_keys`), the cohort's answer rows
 * (`cohort_check_answers`), and the four orientation-level check settings off
 * `visible_orientations`.
 *
 * `visible_orientation_check_keys` is admin-scoped in the module, not catalog-
 * scoped (catalog scope unions the caller's own site memberships, which would
 * hand every answer key to any attached Member). A Member-role session gets an
 * empty map here and the queue renders picks with no correct/missed marks.
 *
 * `beatsByStepVersion` carries the module's scoring denominator rule: a check
 * authored past the end of a step's script AT THE ASSIGNMENT'S VERSION is never
 * presented, so it must not count as wrong. Mirrors
 * `assignments::count_step_beats` (distinct beats on `script_action`).
 *
 * The three check views live in their own subscription batch (they only exist
 * once the checks republish has landed on an org), so `isReady` here is not a
 * readiness gate for any page: it says whether check data has arrived, and a
 * page must still render without it.
 */
export type OrientationCheckSettings = {
  passingScorePct: number | null;
  autoReassignOnFail: boolean;
  maxAttempts: number | null;
};

export function useCheckReview() {
  const [checkRows, checksReady] = useTable(
    'minetwin',
    minetwinTables.visible_orientation_checks,
  );
  const [keyRows, keysReady] = useTable(
    'minetwin',
    minetwinTables.visible_orientation_check_keys,
  );
  const [answerRows, answersReady] = useTable(
    'minetwin',
    minetwinTables.cohort_check_answers,
  );
  const [orientationRows, orientationsReady] = useTable(
    'minetwin',
    minetwinTables.visible_orientations,
  );
  const [actionRows] = useTable('minetwin', minetwinTables.visible_script_actions);

  const checksByStepId = useMemo(() => {
    const m = new Map<string, ReviewCheckDef[]>();
    for (const r of checkRows) {
      const list = m.get(r.stepId) ?? [];
      list.push({
        checkId: r.checkId,
        stepId: r.stepId,
        beat: r.beat,
        question: r.question,
        options: r.options,
        multiSelect: r.multiSelect,
        grading: r.grading.tag,
        sequenceOrder: r.sequenceOrder,
      });
      m.set(r.stepId, list);
    }
    // Presentation order within a step: the beat it fires on, then the sparse
    // sequence_order the module assigns on save. Several checks may share a
    // beat, so the tiebreak is load-bearing.
    for (const list of m.values()) {
      list.sort((a, b) => a.beat - b.beat || a.sequenceOrder - b.sequenceOrder);
    }
    return m;
  }, [checkRows]);

  const beatsByStepVersion = useMemo(() => {
    const beats = new Map<string, Map<string, Set<number>>>();
    for (const r of actionRows) {
      const byVersion = beats.get(r.stepId) ?? new Map<string, Set<number>>();
      const set = byVersion.get(r.orientationVersion.tag) ?? new Set<number>();
      set.add(r.beat);
      byVersion.set(r.orientationVersion.tag, set);
      beats.set(r.stepId, byVersion);
    }
    const m = new Map<string, Map<string, number>>();
    for (const [stepId, byVersion] of beats) {
      const counts = new Map<string, number>();
      for (const [tag, set] of byVersion) counts.set(tag, set.size);
      m.set(stepId, counts);
    }
    return m;
  }, [actionRows]);

  const correctByCheckId = useMemo(() => {
    const m = new Map<string, readonly string[]>();
    for (const r of keyRows) m.set(r.checkId, r.correctOptionIds);
    return m;
  }, [keyRows]);

  const answersByAssignment = useMemo(() => {
    const m = new Map<string, ReviewAnswer[]>();
    for (const r of answerRows) {
      const key = r.assignmentId.toString();
      const list = m.get(key) ?? [];
      list.push({
        attemptNumber: r.attemptNumber,
        checkId: r.checkId,
        tryNumber: r.tryNumber,
        selectedOptionIds: r.selectedOptionIds,
        correct: r.correct,
        expired: r.expired,
        answeredAtMs: toMillis(r.answeredAt),
      });
      m.set(key, list);
    }
    return m;
  }, [answerRows]);

  const settingsByOrientationId = useMemo(() => {
    const m = new Map<string, OrientationCheckSettings>();
    for (const r of orientationRows) {
      m.set(r.orientationId, {
        passingScorePct: r.passingScorePct ?? null,
        autoReassignOnFail: r.autoReassignOnFail,
        maxAttempts: r.maxAttempts ?? null,
      });
    }
    return m;
  }, [orientationRows]);

  return {
    checksByStepId,
    beatsByStepVersion,
    correctByCheckId,
    answersByAssignment,
    settingsByOrientationId,
    isReady: checksReady && keysReady && answersReady && orientationsReady,
  };
}
